/**
 * Utilitários compartilhados pelos extractors de commerce.
 * SSR-safe: sem acesso obrigatório a window/document.
 */

const SENSITIVE_PATTERN =
  /\b(card|cardnumber|card_number|cc[-_]?number|credit[-_]?card|cvv|cvc|security(?:code)?|expir(?:y|ation)|exp(?:date|iry)?|password|senha|secret|token)\b/i;

export function isBrowserElement(value: unknown): value is Element {
  return typeof Element !== 'undefined' && value instanceof Element;
}

export function isHtmlFormElement(value: unknown): value is HTMLFormElement {
  return (
    typeof HTMLFormElement !== 'undefined' && value instanceof HTMLFormElement
  );
}

export function isHtmlInputElement(value: unknown): value is HTMLInputElement {
  return (
    typeof HTMLInputElement !== 'undefined' && value instanceof HTMLInputElement
  );
}

export function trimNonEmpty(value: unknown): string | undefined {
  if (value == null) return undefined;
  const text = String(value).trim();
  return text.length > 0 ? text : undefined;
}

/**
 * Normaliza preço para number decimal.
 * Exemplos: "R$ 197,00" → 197 · "197.00" → 197 · "19700" → 19700
 * Não divide automaticamente por 100 (centavos).
 */
export function normalizePrice(value: unknown): number | undefined {
  const raw = trimNonEmpty(value);
  if (!raw) return undefined;

  let cleaned = raw.replace(/[^\d.,-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === ',') {
    return undefined;
  }

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');

  if (hasComma && hasDot) {
    // "1.234,56" (BR) vs "1,234.56" (US) — último separador é decimal.
    if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (hasComma) {
    cleaned = cleaned.replace(',', '.');
  }

  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed)) return undefined;
  return parsed;
}

/**
 * Normaliza quantity para number.
 * Inválido ou <= 0 → undefined (não preenche default nesta etapa).
 */
export function normalizeQuantity(value: unknown): number | undefined {
  const raw = trimNonEmpty(value);
  if (!raw) return undefined;

  const parsed = Number(raw.replace(',', '.'));
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return parsed;
}

export function isSensitiveField(el: Element): boolean {
  if (isHtmlInputElement(el) && el.type === 'password') return true;

  const haystack = [
    el.getAttribute('name'),
    el.getAttribute('id'),
    el.getAttribute('autocomplete'),
    el.getAttribute('placeholder'),
    el.getAttribute('aria-label'),
  ]
    .filter(Boolean)
    .join(' ');

  return SENSITIVE_PATTERN.test(haystack);
}

export function readAttribute(
  element: Element,
  names: readonly string[]
): string | undefined {
  for (const name of names) {
    if (!name) continue;
    const attrName = name.startsWith('data-') ? name : name;
    const value = trimNonEmpty(element.getAttribute(attrName));
    if (value) return value;
  }
  return undefined;
}

export function readInputValue(el: Element): string | undefined {
  if (isSensitiveField(el)) return undefined;

  if (isHtmlInputElement(el) || el instanceof HTMLTextAreaElement) {
    return trimNonEmpty(el.value);
  }

  if (el instanceof HTMLSelectElement) {
    return trimNonEmpty(el.value);
  }

  return undefined;
}

export type FindFieldValueOptions = {
  /** Não lê input hidden — customer não deve herdar product_codename / account_id. */
  ignoreHidden?: boolean;
  /** Recusa name/id de produto (sku, offer, codename…). */
  denyNamePattern?: RegExp;
  /** Só name/id exatos — sem token (evita extra_purchase_price → price). */
  exactOnly?: boolean;
};

/**
 * Busca valor de campo por name/id (exato, depois token) dentro de um escopo.
 * Inclui o próprio `root` quando ele é um input/textarea/select.
 */
export function looksLikeCssSelector(value: string): boolean {
  return /^[#.[:]/.test(value) || /[\s>+~]/.test(value);
}

export function findCssFieldValue(
  root: Element,
  selectors: readonly string[]
): string | undefined {
  for (const selector of selectors) {
    if (!selector || !looksLikeCssSelector(selector)) continue;
    try {
      const match = root.matches(selector)
        ? root
        : root.querySelector(selector);
      if (!match) continue;
      const value = readInputValue(match);
      if (value) return value;
    } catch {
      // seletor CSS inválido — ignora
    }
  }
  return undefined;
}

export function findFieldValue(
  root: Element,
  candidates: readonly string[],
  options: FindFieldValueOptions = {}
): string | undefined {
  const allowed = (el: Element | null): el is Element => {
    if (!el) return false;
    if (options.ignoreHidden && isHiddenField(el)) return false;
    if (
      options.denyNamePattern &&
      isDeniedFieldName(el, options.denyNamePattern)
    ) {
      return false;
    }
    return true;
  };

  for (const candidate of candidates) {
    if (!candidate) continue;

    const exact = root.querySelector(
      `input[name="${cssEscape(candidate)}"], textarea[name="${cssEscape(candidate)}"], select[name="${cssEscape(candidate)}"], input[id="${cssEscape(candidate)}"]`
    );
    if (exact && allowed(exact)) {
      const value = readInputValue(exact);
      if (value) return value;
    }
  }

  const inputs: Element[] = [];
  if (
    isHtmlInputElement(root) ||
    root instanceof HTMLTextAreaElement ||
    root instanceof HTMLSelectElement
  ) {
    inputs.push(root);
  }
  inputs.push(...Array.from(root.querySelectorAll('input, textarea, select')));

  if (options.exactOnly) {
    return undefined;
  }

  for (const candidate of candidates) {
    const needle = candidate.toLowerCase();
    for (const input of inputs) {
      if (!allowed(input)) continue;
      if (isSensitiveField(input)) continue;
      const name = (input.getAttribute('name') ?? '').toLowerCase();
      const id = (input.getAttribute('id') ?? '').toLowerCase();
      if (!fieldNameMatches(name, id, needle)) continue;
      const value = readInputValue(input);
      if (value) return value;
    }
  }

  return undefined;
}

export function isHiddenField(el: Element): boolean {
  return isHtmlInputElement(el) && el.type === 'hidden';
}

export function isDeniedFieldName(el: Element, pattern: RegExp): boolean {
  const name = el.getAttribute('name') ?? '';
  const id = el.getAttribute('id') ?? '';
  return pattern.test(name) || pattern.test(id);
}

/**
 * `name` não pode pegar `product_codename`; `address` não pode pegar `emailaddress`.
 * Chaves curtas (id, tel, qty…) só batem exact.
 */
export function fieldNameMatches(
  name: string,
  id: string,
  needle: string
): boolean {
  if (!needle) return false;
  if (name === needle || id === needle) return true;
  if (needle.length < 4) return false;
  return containsAsFieldToken(name, needle) || containsAsFieldToken(id, needle);
}

function containsAsFieldToken(haystack: string, needle: string): boolean {
  if (!haystack) return false;
  let from = 0;
  while (from <= haystack.length) {
    const idx = haystack.indexOf(needle, from);
    if (idx < 0) return false;
    const before = idx === 0 ? '' : haystack.charAt(idx - 1);
    const afterIndex = idx + needle.length;
    const after =
      afterIndex >= haystack.length ? '' : haystack.charAt(afterIndex);
    if (isFieldTokenBoundary(before) && isFieldTokenBoundary(after)) {
      return true;
    }
    from = idx + 1;
  }
  return false;
}

function isFieldTokenBoundary(char: string): boolean {
  return char === '' || /[^a-z0-9]/.test(char);
}

function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/["\\]/g, '\\$&');
}

export function mergeDefined<T extends Record<string, unknown>>(
  ...sources: Array<Partial<T> | null | undefined>
): T {
  const result: Record<string, unknown> = {};
  for (const source of sources) {
    if (!source) continue;
    for (const [key, value] of Object.entries(source)) {
      if (value === undefined || value === null || value === '') continue;
      if (result[key] !== undefined) continue; // prioridade: primeiro preenchido vence
      result[key] = value;
    }
  }
  return result as T;
}

export function uniqueNames(
  custom: readonly string[] | undefined,
  defaults: readonly string[]
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const name of [...(custom ?? []), ...defaults]) {
    const trimmed = name?.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
}
